import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { orchestratorApi, authFetch } from '../api/orchestrator'

interface Entity {
  id: string;
  name: string;
  phone: string;
  status: 'connected' | 'disconnected';
}

interface Recipient {
  phone_number: string;
  name: string;
  creation_date?: Date;
  last_inbound_at?: Date;
  zoho_product?: string | null;
  zoho_lead_created_at?: string | null;
  lead_status?: string | null;
  lead_owner?: string | null;
}

interface Chunk {
  id: number;
  recipients: Recipient[];
  selected: boolean;
}

interface Campaign {
  id: number;
  name: string;
  message: string;
  status: string;
  delivery_mode?: 'immediate' | 'daily';
  daily_recipient_limit?: number | null;
  schedule_time_sgt?: string | null;
  processed_count: number;
  success_count: number;
  failed_count: number;
  total_recipients: number;
  created_at?: string;
  entity_id?: string;
}

interface SavedFilterPreset {
  id: string;
  name: string;
  productFilters: string[];
  leadStatusFilters: string[];
  excludedLeadStatusFilters: string[];
  leadOwnerFilters: string[];
  leadDateFrom: string;
  leadDateTo: string;
  updatedAt: string;
}

type RecipientFilterKey = 'products' | 'statuses' | 'excludedStatuses' | 'owners' | 'dateFrom' | 'dateTo';

interface RecipientFilterStage {
  key: RecipientFilterKey;
  label: string;
  count: number;
}

interface BroadcastProgress {
  campaignId: number;
  status: 'running' | 'paused' | 'completed' | 'failed';
  totalRecipients: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  percentageComplete: number;
  successRate: number;
  failureRate: number;
  currentRecipient?: {
    name: string;
    phone: string;
  };
  lastProcessedRecipient?: {
    name: string;
    phone: string;
    success: boolean;
    timestamp: Date;
  };
}

const getLocalDateBoundary = (value: string, endOfDay = false): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
};

export const Broadcast: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();

  const normalizeZohoPhoneNumber = (phoneNumber: string): string => {
    const digitsOnly = phoneNumber.replace(/[^\d+]/g, '').replace(/^\+/, '');
    if (/^\d{8}$/.test(digitsOnly)) {
      return `65${digitsOnly}`;
    }
    return digitsOnly;
  };

  // Entity selection (same pattern as ChatBoard)
  const [entities, setEntities] = useState<Entity[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string>(() => {
    return localStorage.getItem('selectedEntityId') || '';
  });

  const [broadcastSource, setBroadcastSource] = useState<"csv" | "zoho" | "contacts">("contacts");
  const [chunkSize, setChunkSize] = useState<number>(100);
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [allRecipients, setAllRecipients] = useState<Recipient[]>([]);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);
  const [showPreview, setShowPreview] = useState<number | null>(null);
  const [broadcastProgress, setBroadcastProgress] = useState<BroadcastProgress | null>(null);
  const [pastCampaigns, setPastCampaigns] = useState<Campaign[]>([]);
  const [showPastCampaigns, setShowPastCampaigns] = useState(false);
  const [loadingPastCampaigns, setLoadingPastCampaigns] = useState(false);
  const [syncingLeadStatuses, setSyncingLeadStatuses] = useState(false);
  const [productFilters, setProductFilters] = useState<string[]>([]);
  const [leadStatusFilters, setLeadStatusFilters] = useState<string[]>([]);
  const [excludedLeadStatusFilters, setExcludedLeadStatusFilters] = useState<string[]>([]);
  const [leadOwnerFilters, setLeadOwnerFilters] = useState<string[]>([]);
  const [leadDateFrom, setLeadDateFrom] = useState('');
  const [leadDateTo, setLeadDateTo] = useState('');
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);
  const [availableLeadStatuses, setAvailableLeadStatuses] = useState<string[]>([]);
  const [availableLeadOwners, setAvailableLeadOwners] = useState<string[]>([]);
  const [fetchStatusText, setFetchStatusText] = useState('');
  const [savedFilters, setSavedFilters] = useState<SavedFilterPreset[]>([]);
  const [filterPresetName, setFilterPresetName] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<'immediate' | 'daily'>('immediate');
  const [dailyRecipientLimit, setDailyRecipientLimit] = useState<number>(250);
  const [dailySendTimeSgt, setDailySendTimeSgt] = useState('20:00');

  const chunkSizeOptions = [10, 20, 25, 50, 100, 200, 500, 1000];
  const filterStorageScope = `${selectedEntityId || 'no-entity'}:${broadcastSource}`;
  const savedFiltersStorageKey = `broadcastSavedFilters:${filterStorageScope}`;
  const lastUsedFiltersStorageKey = `broadcastLastUsedFilters:${filterStorageScope}`;

  const getAuthHeaders = async (): Promise<HeadersInit> => {
    return orchestratorApi.getAuthHeaders();
  };

  // Load past campaigns
  const loadPastCampaigns = async () => {
    setLoadingPastCampaigns(true);
    try {
      const response = await fetch('/api/broadcast/campaigns', {
        headers: await getAuthHeaders(),
      });
      const data = await response.json();
      if (data.ok) {
        setPastCampaigns(data.campaigns);
        setShowPastCampaigns(true);
      }
    } catch (error) {
      console.error('Failed to load past campaigns:', error);
    }
    setLoadingPastCampaigns(false);
  };

  const loadCampaignMessage = (campaign: Campaign) => {
    setCampaignName(campaign.name);
    setBroadcastMessage(campaign.message);
    setShowPastCampaigns(false);
  };

  // Load entities on mount (same as ChatBoard)
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    const loadEntities = async () => {
      setEntitiesLoading(true);
      setEntitiesError(null);

      try {
        const authHeaders = await orchestratorApi.getAuthHeaders();
        const response = await fetch('/api/entities', { headers: authHeaders });
        const data = await response.json();

        if (data.ok && data.entities) {
          const formatted: Entity[] = data.entities.map((e: any) => ({
            id: e.id || e.phone,
            name: e.name,
            phone: e.phone,
            status: e.status === 'connected' ? 'connected' : 'disconnected',
          }));
          setEntities(formatted);

          // Restore saved entity or pick first connected
          const saved = localStorage.getItem('selectedEntityId');
          if (saved && formatted.some(e => e.id === saved)) {
            setSelectedEntityId(saved);
          } else if (formatted.length > 0) {
            const connected = formatted.find(e => e.status === 'connected');
            setSelectedEntityId((connected || formatted[0]).id);
          }
        } else {
          setEntities([]);
          setEntitiesError(data.error || 'Failed to load WhatsApp accounts');
        }
      } catch (error) {
        console.error('Failed to load entities:', error);
        setEntities([]);
        setEntitiesError(error instanceof Error ? error.message : 'Failed to load WhatsApp accounts');
      } finally {
        setEntitiesLoading(false);
      }
    };

    loadEntities();
  }, [authLoading, isAuthenticated]);

  // Persist entity selection
  const handleEntitySelect = (entityId: string) => {
    setSelectedEntityId(entityId);
    localStorage.setItem('selectedEntityId', entityId);
    // Clear recipients when entity changes — they belong to the previous entity
    setAllRecipients([]);
    setChunks([]);
    setProductFilters([]);
    setLeadStatusFilters([]);
    setExcludedLeadStatusFilters([]);
    setLeadOwnerFilters([]);
    setLeadDateFrom('');
    setLeadDateTo('');
    setFetchStatusText('');
  };

  const applyFilterPreset = (preset: Pick<SavedFilterPreset, 'productFilters' | 'leadStatusFilters' | 'excludedLeadStatusFilters' | 'leadOwnerFilters' | 'leadDateFrom' | 'leadDateTo'>) => {
    setProductFilters(preset.productFilters || []);
    setLeadStatusFilters(preset.leadStatusFilters || []);
    setExcludedLeadStatusFilters(preset.excludedLeadStatusFilters || []);
    setLeadOwnerFilters(preset.leadOwnerFilters || []);
    setLeadDateFrom(preset.leadDateFrom || '');
    setLeadDateTo(preset.leadDateTo || '');
  };

  const clearFilters = () => {
    setProductFilters([]);
    setLeadStatusFilters([]);
    setExcludedLeadStatusFilters([]);
    setLeadOwnerFilters([]);
    setLeadDateFrom('');
    setLeadDateTo('');
  };

  useEffect(() => {
    try {
      const rawSavedFilters = localStorage.getItem(savedFiltersStorageKey);
      const parsedSavedFilters = rawSavedFilters ? JSON.parse(rawSavedFilters) : [];
      setSavedFilters(Array.isArray(parsedSavedFilters) ? parsedSavedFilters : []);

      const rawLastUsed = localStorage.getItem(lastUsedFiltersStorageKey);
      if (rawLastUsed) {
        applyFilterPreset(JSON.parse(rawLastUsed));
      } else {
        clearFilters();
      }
    } catch (error) {
      console.error('Failed to restore saved broadcast filters:', error);
      setSavedFilters([]);
      clearFilters();
    }
  }, [savedFiltersStorageKey, lastUsedFiltersStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(
        lastUsedFiltersStorageKey,
        JSON.stringify({
          productFilters,
          leadStatusFilters,
          excludedLeadStatusFilters,
          leadOwnerFilters,
          leadDateFrom,
          leadDateTo,
        })
      );
    } catch (error) {
      console.error('Failed to persist last-used broadcast filters:', error);
    }
  }, [lastUsedFiltersStorageKey, productFilters, leadStatusFilters, excludedLeadStatusFilters, leadOwnerFilters, leadDateFrom, leadDateTo]);

  const saveCurrentFilters = () => {
    const trimmedName = filterPresetName.trim();
    if (!trimmedName) {
      alert('Please enter a preset name first');
      return;
    }

    const nextPreset: SavedFilterPreset = {
      id: `${Date.now()}`,
      name: trimmedName,
      productFilters,
      leadStatusFilters,
      excludedLeadStatusFilters,
      leadOwnerFilters,
      leadDateFrom,
      leadDateTo,
      updatedAt: new Date().toISOString(),
    };

    const nextSavedFilters = [
      nextPreset,
      ...savedFilters.filter(preset => preset.name.toLowerCase() !== trimmedName.toLowerCase())
    ];

    setSavedFilters(nextSavedFilters);
    localStorage.setItem(savedFiltersStorageKey, JSON.stringify(nextSavedFilters));
    setFilterPresetName('');
  };

  const deleteSavedFilter = (presetId: string) => {
    const nextSavedFilters = savedFilters.filter(preset => preset.id !== presetId);
    setSavedFilters(nextSavedFilters);
    localStorage.setItem(savedFiltersStorageKey, JSON.stringify(nextSavedFilters));
  };

  // Listen for broadcast progress events
  useEffect(() => {
    const handleBroadcastProgress = (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const progressData = customEvent.detail;

      if (progressData?.type === 'broadcast_progress' && progressData?.progress) {
        const progress = progressData.progress;
        if (progress.lastProcessedRecipient?.timestamp) {
          progress.lastProcessedRecipient.timestamp = new Date(progress.lastProcessedRecipient.timestamp);
        }
        setBroadcastProgress(progress);

        if (activeCampaign && progress.campaignId === activeCampaign.id) {
          setActiveCampaign(prev => prev ? {
            ...prev,
            status: progress.status,
            processed_count: progress.processedCount,
            success_count: progress.successCount,
            failed_count: progress.failedCount
          } : null);
        }

        if (progress.status === 'completed' || progress.status === 'failed') {
          setTimeout(() => {
            setBroadcastProgress(null);
            setActiveCampaign(null);
          }, 5000);
        }
      }
    };

    window.addEventListener('websocket-event', handleBroadcastProgress);
    return () => window.removeEventListener('websocket-event', handleBroadcastProgress);
  }, [activeCampaign]);

  // Fetch recipients based on source (scoped to selected entity)
  const fetchRecipients = async () => {
    if (!selectedEntityId) {
      alert('Please select an entity first');
      return;
    }

    setLoading(true);
    setFetchStatusText('');
    try {
      const authHeaders = await getAuthHeaders();

      if (broadcastSource === "contacts") {
        const pageSize = 300;
        let nextOffset = 0;
        let total = 0;
        let hasMore = true;
        let accumulatedContacts: Recipient[] = [];

        while (hasMore) {
          const response = await fetch(
            `/api/broadcast/recipients?entityId=${encodeURIComponent(selectedEntityId)}&limit=${pageSize}&offset=${nextOffset}`,
            { headers: authHeaders }
          );
          const data = await response.json();

          if (!data.ok || !data.chats) {
            setFetchStatusText('');
            alert('Failed to fetch contacts. Please try again.');
            setLoading(false);
            return;
          }

          const contacts = data.chats.map((chat: any) => ({
            phone_number: chat.phone_number,
            name: chat.name || chat.phone_number,
            last_inbound_at: chat.last_inbound_at ? new Date(chat.last_inbound_at) : undefined,
            zoho_product: chat.zoho_product || null,
            zoho_lead_created_at: chat.zoho_lead_created_at || null,
            lead_status: chat.lead_status || null,
            lead_owner: chat.lead_owner || null,
          }));

          accumulatedContacts = accumulatedContacts.concat(contacts);
          total = data.total || accumulatedContacts.length;
          nextOffset = data.nextOffset || accumulatedContacts.length;
          hasMore = Boolean(data.hasMore);

          setAllRecipients([...accumulatedContacts]);
          setFetchStatusText(`Loaded ${accumulatedContacts.length.toLocaleString()} of ${total.toLocaleString()} recipients...`);

          if (hasMore) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }

        setFetchStatusText(`Loaded ${accumulatedContacts.length.toLocaleString()} recipients.`);
        if (accumulatedContacts.length === 0) {
          alert('No contacts found for this entity. Make sure some contacts have messaged this account.');
        }
      } else if (broadcastSource === "zoho") {
        const testResponse = await fetch('/api/broadcast/test-zoho', {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json'
          }
        });
        const testData = await testResponse.json();

        if (!testData.connected) {
          alert('CRM connection failed. Please check configuration.');
          setLoading(false);
          return;
        }

        const zohoResponse = await fetch(`/api/admin/zoho-crm/leads?limit=10000`, {
          headers: authHeaders,
        });
        const zohoData = await zohoResponse.json();

        if (zohoData.ok) {
          const recipients =
            (zohoData.leads || [])
              .map((lead: any) => {
                const firstName = lead.First_Name?.trim() || '';
                const lastName = lead.Last_Name?.trim() || '';
                const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
                const phoneNumber = normalizeZohoPhoneNumber(lead.Phone || lead.Mobile || lead.Primary_Phone || '');

                if (!phoneNumber) {
                  return null;
                }

                return {
                  phone_number: phoneNumber,
                  name: fullName || lead.Full_Name || lead.Company || phoneNumber,
                  creation_date: lead.Created_Time ? new Date(lead.Created_Time) : undefined,
                  zoho_product: lead.Product || lead.Product__c || null,
                  lead_status: lead.Lead_Status || null,
                  lead_owner: typeof lead.Owner === 'object' ? (lead.Owner?.name || lead.Owner?.full_name || null) : null,
                };
              })
              .filter((lead: Recipient | null): lead is Recipient => Boolean(lead));

          setAllRecipients(recipients);
          setFetchStatusText(`Loaded ${recipients.length.toLocaleString()} CRM recipients.`);
        } else {
          setFetchStatusText(
            `CRM temporarily unavailable${zohoData.error ? `: ${zohoData.error}` : ''}. ` +
            `${allRecipients.length > 0 ? 'The existing recipient list was kept. ' : ''}Please try again.`
          );
        }
      } else {
        setAllRecipients([]);
        setFetchStatusText('');
      }
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
      setFetchStatusText(
        `CRM temporarily unavailable. ` +
        `${allRecipients.length > 0 ? 'The existing recipient list was kept. ' : ''}Please try again.`
      );
    }
    setLoading(false);
  };

  const syncLeadStatusesFromZoho = async () => {
    setSyncingLeadStatuses(true);
    try {
      const response = await fetch('/api/admin/zoho/sync-products', {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders()),
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.ok) {
        alert(data.error || 'Failed to sync lead data from CRM.');
        return;
      }

      alert(`CRM sync completed. ${data.result?.leadsProcessed || 0} leads scanned and ${data.result?.chatsUpdated || 0} chats updated.`);
      await fetchRecipients();
    } catch (error) {
      console.error('Failed to sync lead data from CRM:', error);
      alert('Failed to sync lead data from CRM.');
    } finally {
      setSyncingLeadStatuses(false);
    }
  };

  // Load available Zoho products for filter
  useEffect(() => {
    const query = selectedEntityId ? `?entityId=${encodeURIComponent(selectedEntityId)}` : '';
    authFetch(`/api/admin/zoho/products${query}`)
      .then(r => r.ok ? r.json() : { products: [] })
      .then(d => setAvailableProducts(d.products || []))
      .catch(() => {});

    authFetch(`/api/admin/zoho/lead-statuses${query}`)
      .then(r => r.ok ? r.json() : { lead_statuses: [] })
      .then(d => setAvailableLeadStatuses(d.lead_statuses || []))
      .catch(() => {});

    authFetch(`/api/admin/zoho/lead-owners${query}`)
      .then(r => r.ok ? r.json() : { lead_owners: [] })
      .then(d => setAvailableLeadOwners(d.lead_owners || []))
      .catch(() => {});
  }, [selectedEntityId]);

  // Filter recipients by product and lead date
  const filteredRecipients = useMemo(() => {
    let filtered = allRecipients;
    if (productFilters.length > 0) {
      filtered = filtered.filter(r => r.zoho_product && productFilters.includes(r.zoho_product.trim()));
    }
    if (leadStatusFilters.length > 0) {
      filtered = filtered.filter(r => r.lead_status && leadStatusFilters.includes(r.lead_status.trim()));
    }
    if (excludedLeadStatusFilters.length > 0) {
      filtered = filtered.filter(r => !r.lead_status || !excludedLeadStatusFilters.includes(r.lead_status.trim()));
    }
    if (leadOwnerFilters.length > 0) {
      filtered = filtered.filter(r => r.lead_owner && leadOwnerFilters.includes(r.lead_owner.trim()));
    }
    if (leadDateFrom) {
      const from = getLocalDateBoundary(leadDateFrom);
      filtered = filtered.filter(r =>
        (r.zoho_lead_created_at || r.creation_date) &&
        new Date(r.zoho_lead_created_at || r.creation_date || 0) >= from
      );
    }
    if (leadDateTo) {
      const to = getLocalDateBoundary(leadDateTo, true);
      filtered = filtered.filter(r =>
        (r.zoho_lead_created_at || r.creation_date) &&
        new Date(r.zoho_lead_created_at || r.creation_date || 0) <= to
      );
    }
    return filtered;
  }, [allRecipients, productFilters, leadStatusFilters, excludedLeadStatusFilters, leadOwnerFilters, leadDateFrom, leadDateTo]);

  const filterBreakdown = useMemo(() => {
    let candidates = allRecipients;
    const stages: RecipientFilterStage[] = [];

    if (productFilters.length > 0) {
      candidates = candidates.filter(r => r.zoho_product && productFilters.includes(r.zoho_product.trim()));
      stages.push({ key: 'products', label: 'Products', count: candidates.length });
    }
    if (leadStatusFilters.length > 0) {
      candidates = candidates.filter(r => r.lead_status && leadStatusFilters.includes(r.lead_status.trim()));
      stages.push({ key: 'statuses', label: 'Included Lead Statuses', count: candidates.length });
    }
    if (excludedLeadStatusFilters.length > 0) {
      candidates = candidates.filter(r => !r.lead_status || !excludedLeadStatusFilters.includes(r.lead_status.trim()));
      stages.push({ key: 'excludedStatuses', label: 'Excluded Lead Statuses', count: candidates.length });
    }
    if (leadOwnerFilters.length > 0) {
      candidates = candidates.filter(r => r.lead_owner && leadOwnerFilters.includes(r.lead_owner.trim()));
      stages.push({ key: 'owners', label: 'Lead Owners', count: candidates.length });
    }
    if (leadDateFrom) {
      const from = getLocalDateBoundary(leadDateFrom);
      candidates = candidates.filter(r =>
        (r.zoho_lead_created_at || r.creation_date) &&
        new Date(r.zoho_lead_created_at || r.creation_date || 0) >= from
      );
      stages.push({ key: 'dateFrom', label: `Lead from ${leadDateFrom}`, count: candidates.length });
    }
    if (leadDateTo) {
      const to = getLocalDateBoundary(leadDateTo, true);
      candidates = candidates.filter(r =>
        (r.zoho_lead_created_at || r.creation_date) &&
        new Date(r.zoho_lead_created_at || r.creation_date || 0) <= to
      );
      stages.push({ key: 'dateTo', label: `Lead to ${leadDateTo}`, count: candidates.length });
    }

    return stages;
  }, [allRecipients, productFilters, leadStatusFilters, excludedLeadStatusFilters, leadOwnerFilters, leadDateFrom, leadDateTo]);

  const firstEmptyFilterStage = useMemo(() => {
    const index = filterBreakdown.findIndex(stage => stage.count === 0);
    if (index < 0) {
      return null;
    }
    return {
      stage: filterBreakdown[index],
      previousCount: index === 0 ? allRecipients.length : filterBreakdown[index - 1].count,
    };
  }, [allRecipients.length, filterBreakdown]);

  const hasRecipientFilters = productFilters.length > 0
    || leadStatusFilters.length > 0
    || excludedLeadStatusFilters.length > 0
    || leadOwnerFilters.length > 0
    || Boolean(leadDateFrom)
    || Boolean(leadDateTo);

  const removeFilterStage = (key: RecipientFilterKey) => {
    switch (key) {
      case 'products':
        setProductFilters([]);
        break;
      case 'statuses':
        setLeadStatusFilters([]);
        break;
      case 'excludedStatuses':
        setExcludedLeadStatusFilters([]);
        break;
      case 'owners':
        setLeadOwnerFilters([]);
        break;
      case 'dateFrom':
        setLeadDateFrom('');
        break;
      case 'dateTo':
        setLeadDateTo('');
        break;
    }
  };

  const visibleLeadStatuses = useMemo(() => {
    const recipientStatuses = allRecipients
      .map(recipient => recipient.lead_status?.trim())
      .filter((status): status is string => Boolean(status));

    return [...new Set([...availableLeadStatuses, ...recipientStatuses])].sort((a, b) => a.localeCompare(b));
  }, [allRecipients, availableLeadStatuses]);

  const productCounts = useMemo(() => {
    const counts = new Map<string, number>();
    allRecipients.forEach(recipient => {
      const product = recipient.zoho_product?.trim();
      if (product) {
        counts.set(product, (counts.get(product) || 0) + 1);
      }
    });
    return counts;
  }, [allRecipients]);

  const leadStatusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    allRecipients.forEach(recipient => {
      const status = recipient.lead_status?.trim();
      if (status) {
        counts.set(status, (counts.get(status) || 0) + 1);
      }
    });
    return counts;
  }, [allRecipients]);

  const leadOwnerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    allRecipients.forEach(recipient => {
      const owner = recipient.lead_owner?.trim();
      if (owner) {
        counts.set(owner, (counts.get(owner) || 0) + 1);
      }
    });
    return counts;
  }, [allRecipients]);

  const productLeadTotal = Array.from(productCounts.values()).reduce((sum, count) => sum + count, 0);
  const leadStatusTotal = Array.from(leadStatusCounts.values()).reduce((sum, count) => sum + count, 0);
  const leadOwnerTotal = Array.from(leadOwnerCounts.values()).reduce((sum, count) => sum + count, 0);

  const toggleProductFilter = (product: string) => {
    setProductFilters(prev =>
      prev.includes(product)
        ? prev.filter(item => item !== product)
        : [...prev, product]
    );
  };

  const toggleLeadStatusFilter = (status: string) => {
    setExcludedLeadStatusFilters(prev => prev.filter(item => item !== status));
    setLeadStatusFilters(prev =>
      prev.includes(status)
        ? prev.filter(item => item !== status)
        : [...prev, status]
    );
  };

  const toggleExcludedLeadStatusFilter = (status: string) => {
    setLeadStatusFilters(prev => prev.filter(item => item !== status));
    setExcludedLeadStatusFilters(prev =>
      prev.includes(status)
        ? prev.filter(item => item !== status)
        : [...prev, status]
    );
  };

  const toggleLeadOwnerFilter = (owner: string) => {
    setLeadOwnerFilters(prev =>
      prev.includes(owner)
        ? prev.filter(item => item !== owner)
        : [...prev, owner]
    );
  };

  const visibleProducts = useMemo(() => {
    const recipientProducts = allRecipients
      .map(recipient => recipient.zoho_product?.trim())
      .filter((product): product is string => Boolean(product));

    return [...new Set([...availableProducts, ...recipientProducts])].sort((a, b) => a.localeCompare(b));
  }, [allRecipients, availableProducts]);

  const visibleLeadOwners = useMemo(() => {
    const recipientOwners = allRecipients
      .map(recipient => recipient.lead_owner?.trim())
      .filter((owner): owner is string => Boolean(owner));

    return [...new Set([...availableLeadOwners, ...recipientOwners])].sort((a, b) => a.localeCompare(b));
  }, [allRecipients, availableLeadOwners]);

  // Create chunks when filtered recipients or chunk size changes
  useEffect(() => {
    if (filteredRecipients.length === 0) {
      setChunks([]);
      return;
    }

    const newChunks: Chunk[] = [];
    for (let i = 0; i < filteredRecipients.length; i += chunkSize) {
      newChunks.push({
        id: Math.floor(i / chunkSize) + 1,
        recipients: filteredRecipients.slice(i, i + chunkSize),
        selected: false
      });
    }
    setChunks(newChunks);
  }, [filteredRecipients, chunkSize]);

  const toggleChunk = (chunkId: number) => {
    setChunks(chunks.map(chunk =>
      chunk.id === chunkId
        ? { ...chunk, selected: !chunk.selected }
        : chunk
    ));
  };

  const selectAllChunks = () => {
    const allSelected = chunks.every(chunk => chunk.selected);
    setChunks(chunks.map(chunk => ({ ...chunk, selected: !allSelected })));
  };

  const getSelectedRecipients = () => {
    return chunks
      .filter(chunk => chunk.selected)
      .flatMap(chunk => chunk.recipients);
  };

  // Start broadcast campaign — always includes selected entity
  const startBroadcast = async () => {
    const selectedRecipients = getSelectedRecipients();

    if (!selectedEntityId) {
      alert('Please select an entity first');
      return;
    }

    if (!campaignName.trim()) {
      alert('Please enter a campaign name');
      return;
    }

    if (!broadcastMessage.trim()) {
      alert('Please enter a broadcast message');
      return;
    }

    if (selectedRecipients.length === 0) {
      alert('Please select at least one chunk to broadcast to');
      return;
    }

    setLoading(true);
    try {
      const authHeaders = await getAuthHeaders();

      // Create campaign with entity_id
      const campaignResponse = await fetch('/api/broadcast/campaigns', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: campaignName,
          message: broadcastMessage,
          source_type: broadcastSource === 'zoho' ? 'zoho' : 'contacts',
          entity_id: selectedEntityId,
          delivery_mode: deliveryMode,
          daily_recipient_limit: deliveryMode === 'daily' ? dailyRecipientLimit : null,
          schedule_time_sgt: deliveryMode === 'daily' ? dailySendTimeSgt : null,
          filter_snapshot: {
            productFilters,
            leadStatusFilters,
            excludedLeadStatusFilters,
            leadOwnerFilters,
            leadDateFrom,
            leadDateTo,
            source: broadcastSource,
          }
        })
      });

      const campaignData = await campaignResponse.json();
      if (!campaignData.ok) {
        alert(`Failed to create campaign: ${campaignData.error || 'Unknown error'}\n${campaignData.details || ''}`);
        setLoading(false);
        return;
      }

      const addRecipientsResponse = await fetch(`/api/broadcast/campaigns/${campaignData.campaign.id}/recipients/custom`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipients: selectedRecipients.map(r => ({
            phone_number: r.phone_number,
            name: r.name
          }))
        })
      });

      const addRecipientsData = await addRecipientsResponse.json();
      if (!addRecipientsResponse.ok || !addRecipientsData.ok) {
        alert(`Failed to add recipients: ${addRecipientsData.error || 'Unknown error'}`);
        setLoading(false);
        return;
      }

      if (!addRecipientsData.totalAdded) {
        await fetch(`/api/broadcast/campaigns/${campaignData.campaign.id}`, {
          method: 'DELETE',
          headers: authHeaders,
        }).catch(() => undefined);
        alert('Every selected recipient was already sent this exact message previously from this WhatsApp account, so nothing new was queued.');
        setLoading(false);
        return;
      }

      const startResponse = await fetch(`/api/broadcast/campaigns/${campaignData.campaign.id}/start`, {
        method: 'POST',
        headers: authHeaders,
      });

      const startData = await startResponse.json();
      if (!startResponse.ok || !startData.ok) {
        alert(`Failed to start campaign: ${startData.error || 'Unknown error'}`);
        setLoading(false);
        return;
      }

      setActiveCampaign(campaignData.campaign);
      const entity = entities.find(e => e.id === selectedEntityId);
      const excludedCount = addRecipientsData.duplicatesExcluded || 0;
      const deliveryNote = deliveryMode === 'daily'
        ? ` It will send up to ${dailyRecipientLimit.toLocaleString()} chats per day until the list is exhausted.`
        : '';
      alert(
        `Campaign "${campaignName}" started for ${entity?.name || selectedEntityId} with ${addRecipientsData.totalAdded.toLocaleString()} queued recipients.` +
        (excludedCount > 0 ? ` ${excludedCount.toLocaleString()} chats were excluded because this exact message was already sent before.` : '') +
        deliveryNote
      );
      setCampaignName("");
      setBroadcastMessage("");
      setDeliveryMode('immediate');
      setDailyRecipientLimit(250);
      setDailySendTimeSgt('20:00');
      setChunks(chunks.map(chunk => ({ ...chunk, selected: false })));
    } catch (error) {
      console.error('Failed to start broadcast:', error);
      alert(`Failed to start broadcast campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    setLoading(false);
  };

  const stopCampaign = async () => {
    if (!activeCampaign) return;

    try {
      const response = await fetch(`/api/broadcast/campaigns/${activeCampaign.id}/stop`, {
        method: 'POST',
        headers: await getAuthHeaders(),
      });

      if (response.ok) {
        setActiveCampaign(null);
        alert('Campaign stopped successfully');
      }
    } catch (error) {
      console.error('Failed to stop campaign:', error);
    }
  };

  const selectedCount = getSelectedRecipients().length;
  const totalChunks = chunks.length;
  const selectedChunks = chunks.filter(chunk => chunk.selected).length;
  const selectedEntity = entities.find(e => e.id === selectedEntityId);

  return (
    <div className="broadcast-container">
      {/* Entity Selector — same style as ChatBoard */}
      <div className="broadcast-entity-bar">
        <label className="entity-label">WhatsApp Account:</label>
        <select
          value={selectedEntityId}
          onChange={(e) => handleEntitySelect(e.target.value)}
          className="entity-select"
          disabled={entitiesLoading || entities.length === 0}
        >
          {entitiesLoading && (
            <option value="">Loading entities...</option>
          )}
          {!entitiesLoading && entities.length === 0 && (
            <option value="">
              {entitiesError ? 'Failed to load accounts' : 'No WhatsApp accounts found'}
            </option>
          )}
          {entities.map(entity => (
            <option key={entity.id} value={entity.id}>
              {entity.status === 'connected' ? '🟢' : '🔴'} {entity.name} ({entity.phone})
            </option>
          ))}
        </select>
        {selectedEntity && (
          <span className={`entity-status-badge ${selectedEntity.status}`}>
            {selectedEntity.status === 'connected' ? '🟢 Connected' : '🔴 Disconnected'}
          </span>
        )}
        {entitiesError && !entitiesLoading && (
          <span className="entity-status-badge disconnected">
            Failed to load accounts
          </span>
        )}
      </div>

      <div className="broadcast-header">
        <h2>📡 Broadcast Messaging</h2>
        <p>Send messages to existing contacts from the selected WhatsApp account</p>
      </div>

      {/* Active Campaign Display with Detailed Progress */}
      {(activeCampaign || broadcastProgress) && (
        <div className="active-campaign">
          <h4>🚀 Active Campaign: {activeCampaign?.name || `Campaign ${broadcastProgress?.campaignId}`}</h4>

          {broadcastProgress && (
            <div className="detailed-progress">
              <div className="progress-section">
                <div className="progress-header">
                  <span className="progress-label">Overall Progress</span>
                  <span className="progress-percentage">{broadcastProgress.percentageComplete}%</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${broadcastProgress.percentageComplete}%` }}
                  />
                </div>
                <div className="progress-stats">
                  <span>📊 {broadcastProgress.processedCount.toLocaleString()} / {broadcastProgress.totalRecipients.toLocaleString()} processed</span>
                </div>
              </div>

              <div className="stats-section">
                <div className="stat-card success">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <div className="stat-number">{broadcastProgress.successCount.toLocaleString()}</div>
                    <div className="stat-label">Successful ({broadcastProgress.successRate}%)</div>
                  </div>
                </div>
                <div className="stat-card failure">
                  <div className="stat-icon">❌</div>
                  <div className="stat-content">
                    <div className="stat-number">{broadcastProgress.failedCount.toLocaleString()}</div>
                    <div className="stat-label">Failed ({broadcastProgress.failureRate}%)</div>
                  </div>
                </div>
              </div>

              {broadcastProgress.currentRecipient && (
                <div className="current-processing">
                  <h5>📞 Currently Processing:</h5>
                  <div className="recipient-info">
                    <span className="recipient-name">{broadcastProgress.currentRecipient.name}</span>
                    <span className="recipient-phone">{broadcastProgress.currentRecipient.phone}</span>
                  </div>
                </div>
              )}

              {broadcastProgress.lastProcessedRecipient && (
                <div className="last-processed">
                  <h5>📝 Last Processed:</h5>
                  <div className="recipient-info">
                    <span className={`recipient-result ${broadcastProgress.lastProcessedRecipient.success ? 'success' : 'failure'}`}>
                      {broadcastProgress.lastProcessedRecipient.success ? '✅' : '❌'}
                    </span>
                    <span className="recipient-name">{broadcastProgress.lastProcessedRecipient.name}</span>
                    <span className="recipient-phone">{broadcastProgress.lastProcessedRecipient.phone}</span>
                    <span className="recipient-time">
                      {broadcastProgress.lastProcessedRecipient.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              )}

              <div className="campaign-status">
                Status: <span className={`status-${broadcastProgress.status}`}>
                  {broadcastProgress.status.toUpperCase()}
                </span>
              </div>
            </div>
          )}

          <div className="campaign-controls">
            <button onClick={stopCampaign} className="campaign-btn stop">
              🛑 Stop Campaign
            </button>
          </div>
        </div>
      )}

      {/* Source Selection */}
      <div className="source-selection">
        <h3>📊 Data Source</h3>
        <div className="source-options">
          <label className="source-option">
            <input
              type="radio"
              value="contacts"
              checked={broadcastSource === "contacts"}
              onChange={(e) => { setBroadcastSource(e.target.value as "contacts"); setFetchStatusText(''); }}
            />
            <span>Existing Contacts</span>
          </label>
          <label className="source-option">
            <input
              type="radio"
              value="zoho"
              checked={broadcastSource === "zoho"}
              onChange={(e) => { setBroadcastSource(e.target.value as "zoho"); setFetchStatusText(''); }}
            />
            <span>CRM</span>
          </label>
          <label className="source-option">
            <input
              type="radio"
              value="csv"
              checked={broadcastSource === "csv"}
              onChange={(e) => setBroadcastSource(e.target.value as "csv")}
              disabled
            />
            <span>CSV Upload (Coming Soon)</span>
          </label>
        </div>

        {broadcastSource === "zoho" && (
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={syncLeadStatusesFromZoho}
              disabled={syncingLeadStatuses}
              className="fetch-btn"
              style={{ marginRight: '10px' }}
            >
              {syncingLeadStatuses ? '⏳ Syncing Lead Data...' : '🔄 Sync Products & Lead Statuses'}
            </button>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Download the latest CRM product and lead status data before filtering.
            </span>
          </div>
        )}

        <button
          onClick={fetchRecipients}
          disabled={loading || !selectedEntityId}
          className="fetch-btn"
          title={!selectedEntityId ? 'Select an entity first' : ''}
        >
          {loading ? '⏳ Loading...' : '📥 Fetch Recipients'}
        </button>

        {!selectedEntityId && (
          <p className="entity-warning">⚠️ Select a WhatsApp account above before fetching recipients.</p>
        )}
        {fetchStatusText && (
          <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
            {fetchStatusText}
          </p>
        )}
      </div>

      {/* Product + Lead Status + Lead Date Filters */}
      {allRecipients.length > 0 && (
        <div className="chunk-settings">
          <h3 className="filter-section-heading">
            <span>🔍 Filter Recipients</span>
            <span className="filter-overall-total">{allRecipients.length.toLocaleString()} total leads</span>
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
            <div>
              <div className="filter-list-heading">
                <span>Products</span>
                <span>{productLeadTotal.toLocaleString()} leads</span>
              </div>
              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  minWidth: '220px',
                  minHeight: '110px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  background: 'white',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                {visibleProducts.map(product => (
                  <label
                    key={product}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      color: '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={productFilters.includes(product)}
                      onChange={() => toggleProductFilter(product)}
                    />
                    <span className="filter-option-name">{product}</span>
                    <span className="filter-option-count">{(productCounts.get(product) || 0).toLocaleString()}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="filter-list-heading filter-list-heading-include">
                <span>Include Lead Status</span>
                <span>{leadStatusTotal.toLocaleString()} leads</span>
              </div>
              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  minWidth: '220px',
                  minHeight: '110px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  background: 'white',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  opacity: visibleLeadStatuses.length === 0 ? 0.6 : 1,
                }}
              >
                {visibleLeadStatuses.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>No Lead Statuses Available</div>
                ) : (
                  visibleLeadStatuses.map(status => (
                    <label
                      key={status}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        color: '#374151',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={leadStatusFilters.includes(status)}
                        onChange={() => toggleLeadStatusFilter(status)}
                      />
                      <span className="filter-option-name">{status}</span>
                      <span className="filter-option-count">{(leadStatusCounts.get(status) || 0).toLocaleString()}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div>
              <div className="filter-list-heading filter-list-heading-exclude">
                <span>Exclude Lead Status</span>
                <span>{leadStatusTotal.toLocaleString()} leads</span>
              </div>
              <div
                style={{
                  border: '1px solid #f0b4b4',
                  borderRadius: '4px',
                  minWidth: '220px',
                  minHeight: '110px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  background: '#fffafa',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  opacity: visibleLeadStatuses.length === 0 ? 0.6 : 1,
                }}
              >
                {visibleLeadStatuses.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>No Lead Statuses Available</div>
                ) : (
                  visibleLeadStatuses.map(status => (
                    <label
                      key={status}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        color: '#374151',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={excludedLeadStatusFilters.includes(status)}
                        onChange={() => toggleExcludedLeadStatusFilter(status)}
                      />
                      <span className="filter-option-name">{status}</span>
                      <span className="filter-option-count filter-option-count-excluded">
                        {(leadStatusCounts.get(status) || 0).toLocaleString()}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div>
              <div className="filter-list-heading">
                <span>Lead Owners</span>
                <span>{leadOwnerTotal.toLocaleString()} leads</span>
              </div>
              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  minWidth: '220px',
                  minHeight: '110px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  background: 'white',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  opacity: visibleLeadOwners.length === 0 ? 0.6 : 1,
                }}
              >
                {visibleLeadOwners.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>No Lead Owners Available</div>
                ) : (
                  visibleLeadOwners.map(owner => (
                    <label
                      key={owner}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        color: '#374151',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={leadOwnerFilters.includes(owner)}
                        onChange={() => toggleLeadOwnerFilter(owner)}
                      />
                      <span className="filter-option-name">{owner}</span>
                      <span className="filter-option-count">{(leadOwnerCounts.get(owner) || 0).toLocaleString()}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <label style={{ fontSize: '12px', color: '#6b7280' }}>Lead from:</label>
              <input
                type="date"
                value={leadDateFrom}
                onChange={(e) => setLeadDateFrom(e.target.value)}
                style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', color: leadDateFrom ? '#1a73e8' : '#374151' }}
              />
              <label style={{ fontSize: '12px', color: '#6b7280' }}>to:</label>
              <input
                type="date"
                value={leadDateTo}
                onChange={(e) => setLeadDateTo(e.target.value)}
                style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', color: leadDateTo ? '#1a73e8' : '#374151' }}
              />
            </div>
            {hasRecipientFilters && (
              <button
                onClick={clearFilters}
                style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}
              >✕ Clear</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
            <input
              type="text"
              value={filterPresetName}
              onChange={(e) => setFilterPresetName(e.target.value)}
              placeholder="Save current filters as..."
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', minWidth: '220px' }}
            />
            <button
              type="button"
              onClick={saveCurrentFilters}
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}
            >
              Save Filters
            </button>
          </div>
          {savedFilters.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {savedFilters.map((preset) => (
                <div
                  key={preset.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '999px',
                    background: '#f8fafc',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => applyFilterPreset(preset)}
                    style={{ background: 'transparent', border: 'none', color: '#1f2937', cursor: 'pointer', fontSize: '12px' }}
                    title={`Updated ${new Date(preset.updatedAt).toLocaleString()}`}
                  >
                    {preset.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSavedFilter(preset.id)}
                    style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}
                    title="Delete saved filter"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px 0' }}>
            Filters combine together. Included statuses are matched first, then excluded statuses are removed. A status cannot be both included and excluded. Your last-used filters are restored automatically for this WhatsApp account and source.
          </p>
          {hasRecipientFilters && (
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px 0' }}>
              Showing <strong>{filteredRecipients.length.toLocaleString()}</strong> of {allRecipients.length.toLocaleString()} recipients
            </p>
          )}
          {hasRecipientFilters && filteredRecipients.length === 0 && firstEmptyFilterStage && (
            <div className="filter-empty-explanation" role="status">
              <strong>No recipients match all selected filters.</strong>
              <span>
                {firstEmptyFilterStage.previousCount.toLocaleString()} matched before{' '}
                <strong>{firstEmptyFilterStage.stage.label}</strong>; that filter leaves 0.
              </span>
              <button
                type="button"
                onClick={() => removeFilterStage(firstEmptyFilterStage.stage.key)}
              >
                Remove {firstEmptyFilterStage.stage.label}
              </button>
            </div>
          )}
          {hasRecipientFilters && filteredRecipients.length > 0 && (
            <details className="filtered-recipient-preview" open>
              <summary>
                Filtered recipients ({filteredRecipients.length.toLocaleString()})
              </summary>
              <div className="filtered-recipient-table-wrap">
                <table className="filtered-recipient-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Product</th>
                      <th>Lead Status</th>
                      <th>Lead Owner</th>
                      <th>Lead Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecipients.slice(0, 50).map((recipient, index) => {
                      const leadDate = recipient.zoho_lead_created_at || recipient.creation_date;
                      return (
                        <tr key={`${recipient.phone_number}-${index}`}>
                          <td>{recipient.name}</td>
                          <td>{recipient.phone_number}</td>
                          <td>{recipient.zoho_product || '—'}</td>
                          <td>{recipient.lead_status || '—'}</td>
                          <td>{recipient.lead_owner || '—'}</td>
                          <td>{leadDate ? new Date(leadDate).toLocaleDateString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredRecipients.length > 50 && (
                <p className="filtered-recipient-limit">
                  Showing the first 50 recipients. All {filteredRecipients.length.toLocaleString()} matches will be included in the chunks below.
                </p>
              )}
            </details>
          )}
          {broadcastSource === "zoho" && visibleLeadStatuses.length > 0 && (
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
              Lead Status values are downloaded directly from your CRM when you fetch recipients.
            </p>
          )}
        </div>
      )}

      {/* Chunk Size Selection */}
      {allRecipients.length > 0 && (
        <div className="chunk-settings">
          <h3>📦 Chunk Settings</h3>
          <div className="chunk-size-selector">
            <label>Chunk Size:</label>
            <select
              value={chunkSize}
              onChange={(e) => setChunkSize(Number(e.target.value))}
              className="chunk-size-select"
            >
              {chunkSizeOptions.map(size => (
                <option key={size} value={size}>{size} recipients per chunk</option>
              ))}
            </select>
            <label style={{ marginLeft: '12px' }}>Custom:</label>
            <input
              type="number"
              min={1}
              step={1}
              value={chunkSize}
              onChange={(e) => setChunkSize(Math.max(1, Number(e.target.value) || 1))}
              style={{ padding: '6px 10px', width: '100px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div className="recipients-summary">
            <span>📋 Total Recipients: <strong>{filteredRecipients.length.toLocaleString()}</strong>{filteredRecipients.length !== allRecipients.length && ` (filtered from ${allRecipients.length.toLocaleString()})`}</span>
            <span>📦 Total Chunks: <strong>{totalChunks}</strong></span>
            <span>✅ Selected: <strong>{selectedCount.toLocaleString()}</strong> recipients ({selectedChunks} chunks)</span>
          </div>
        </div>
      )}

      {/* Chunk Selection */}
      {chunks.length > 0 && (
        <div className="chunk-selection">
          <div className="chunk-header">
            <h3>📋 Select Chunks to Broadcast</h3>
            <button onClick={selectAllChunks} className="select-all-btn">
              {chunks.every(chunk => chunk.selected) ? '❌ Deselect All' : '✅ Select All'}
            </button>
          </div>

          <div className="chunks-grid">
            {chunks.map(chunk => (
              <div key={chunk.id} className={`chunk-card ${chunk.selected ? 'selected' : ''}`}>
                <div className="chunk-header-row">
                  <label className="chunk-checkbox">
                    <input
                      type="checkbox"
                      checked={chunk.selected}
                      onChange={() => toggleChunk(chunk.id)}
                    />
                    <span className="chunk-title">Chunk {chunk.id}</span>
                  </label>
                  <button
                    onClick={() => setShowPreview(showPreview === chunk.id ? null : chunk.id)}
                    className="preview-btn"
                  >
                    {showPreview === chunk.id ? '👁️ Hide' : '👁️ Preview'}
                  </button>
                </div>

                <div className="chunk-info">
                  <span>📞 {chunk.recipients.length} recipients</span>
                </div>

                {showPreview === chunk.id && (
                  <div className="chunk-preview">
                    <h5>📋 Recipients in Chunk {chunk.id}:</h5>
                    <div className="recipients-list">
                      {chunk.recipients.map((recipient, index) => (
                        <div key={index} className="recipient-item">
                          <span className="recipient-name">{recipient.name}</span>
                          <span className="recipient-phone">{recipient.phone_number}</span>
                          {recipient.lead_status ? (
                            <span className="recipient-date" title="CRM Lead Status">
                              {recipient.lead_status}
                            </span>
                          ) : null}
                          {recipient.last_inbound_at ? (
                            <span className="recipient-date" title="Last inbound message">
                              📩 {recipient.last_inbound_at.toLocaleDateString()} {recipient.last_inbound_at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          ) : recipient.creation_date ? (
                            <span className="recipient-date">{recipient.creation_date.toLocaleDateString()}</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Setup */}
      {selectedCount > 0 && (
        <div className="campaign-setup">
          <h3>🚀 Campaign Setup</h3>

          <div className="campaign-form">
            <div className="form-group">
              <label>Load Previous Campaign:</label>
              <div className="past-campaigns-row">
                <button
                  type="button"
                  onClick={loadPastCampaigns}
                  disabled={loadingPastCampaigns}
                  className="load-campaigns-btn"
                >
                  {loadingPastCampaigns ? '⏳ Loading...' : '📂 Browse Previous Campaigns'}
                </button>
                {showPastCampaigns && pastCampaigns.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowPastCampaigns(false)}
                    className="close-campaigns-btn"
                  >
                    ✕ Close
                  </button>
                )}
              </div>
              {showPastCampaigns && (
                <div className="past-campaigns-list">
                  <div className="past-campaigns-note">
                    📋 Showing campaigns from all entities. Selecting one will load its message and be sent from <strong>{selectedEntity?.name || 'the currently selected account'}</strong>.
                  </div>
                  {pastCampaigns.length === 0 ? (
                    <p className="no-campaigns">No previous campaigns found.</p>
                  ) : (
                    pastCampaigns.map(c => {
                      const campaignEntity = entities.find(e => e.id === c.entity_id);
                      return (
                        <div key={c.id} className="past-campaign-item" onClick={() => loadCampaignMessage(c)}>
                          <div className="past-campaign-header">
                            <span className="past-campaign-name">{c.name}</span>
                            <span className={`past-campaign-status status-${c.status}`}>{c.status}</span>
                          </div>
                          <div className="past-campaign-meta">
                            <span>👥 {c.total_recipients} recipients</span>
                            {c.created_at && (
                              <span>📅 {new Date(c.created_at).toLocaleDateString()}</span>
                            )}
                            <span>📱 {campaignEntity?.name || c.entity_id || 'Unknown entity'}</span>
                          </div>
                          <div className="past-campaign-message">{c.message}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Sending from:</label>
              <div className="entity-readonly">
                {selectedEntity
                  ? `${selectedEntity.status === 'connected' ? '🟢' : '🔴'} ${selectedEntity.name} (${selectedEntity.phone})`
                  : '—'
                }
              </div>
            </div>

            <div className="form-group">
              <label>Campaign Name:</label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Enter campaign name"
                className="campaign-name-input"
              />
            </div>

            <div className="form-group">
              <label>Broadcast Message:</label>
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Enter your broadcast message"
                rows={6}
                className="broadcast-message-textarea"
              />
            </div>

            <div className="form-group">
              <label>Campaign Delivery:</label>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label className="source-option">
                  <input
                    type="radio"
                    value="immediate"
                    checked={deliveryMode === 'immediate'}
                    onChange={() => setDeliveryMode('immediate')}
                  />
                  <span>Send Immediately</span>
                </label>
                <label className="source-option">
                  <input
                    type="radio"
                    value="daily"
                    checked={deliveryMode === 'daily'}
                    onChange={() => setDeliveryMode('daily')}
                  />
                  <span>Daily Campaign</span>
                </label>
                {deliveryMode === 'daily' && (
                  <>
                    <label style={{ fontSize: '13px', color: '#374151' }}>Daily chat limit:</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={dailyRecipientLimit}
                      onChange={(e) => setDailyRecipientLimit(Math.max(1, Number(e.target.value) || 1))}
                      style={{ padding: '6px 10px', width: '120px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                    <label style={{ fontSize: '13px', color: '#374151' }}>Send time (SGT):</label>
                    <input
                      type="time"
                      value={dailySendTimeSgt}
                      onChange={(e) => setDailySendTimeSgt(e.target.value || '20:00')}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="campaign-summary">
              <p>📊 <strong>Ready to send to {selectedCount.toLocaleString()} recipients</strong> across {selectedChunks} chunks</p>
              <p className="campaign-note">⚠️ Only contacts who have previously messaged this account will receive the broadcast. Others will be skipped automatically.</p>
              <p className="campaign-note">♻️ Chats that already received this exact message from this WhatsApp account are excluded before the campaign starts.</p>
              <p className="campaign-note">⏱️ Messages are sent with a random delay of 5–15 seconds between each recipient to avoid WhatsApp rate limits.</p>
              {deliveryMode === 'daily' && (
                <p className="campaign-note">📅 This daily campaign will send up to {dailyRecipientLimit.toLocaleString()} successful chats each day at {dailySendTimeSgt} SGT until no recipients remain.</p>
              )}
            </div>

            <button
              onClick={startBroadcast}
              disabled={loading || !campaignName.trim() || !broadcastMessage.trim() || !selectedEntityId || (deliveryMode === 'daily' && dailyRecipientLimit < 1)}
              className="start-broadcast-btn"
            >
              {loading ? '⏳ Starting...' : deliveryMode === 'daily' ? '🚀 Start Daily Broadcast Campaign' : '🚀 Start Broadcast Campaign'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .broadcast-entity-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #1a1a2e;
          border-bottom: 1px solid #2d2d4e;
          margin-bottom: 16px;
        }
        .entity-label {
          font-size: 13px;
          color: #9ca3af;
          white-space: nowrap;
        }
        .entity-select {
          background: #2d2d4e;
          color: #e5e7eb;
          border: 1px solid #4b5563;
          border-radius: 6px;
          padding: 6px 10px;
          font-size: 13px;
          min-width: 200px;
          cursor: pointer;
        }
        .entity-select:focus {
          outline: none;
          border-color: #6366f1;
        }
        .entity-status-badge {
          font-size: 12px;
          padding: 3px 8px;
          border-radius: 12px;
          background: #1f2937;
        }
        .entity-status-badge.connected { color: #34d399; }
        .entity-status-badge.disconnected { color: #f87171; }
        .entity-warning {
          margin-top: 8px;
          font-size: 12px;
          color: #f59e0b;
        }
        .entity-readonly {
          padding: 8px 12px;
          background: #1f2937;
          border: 1px solid #374151;
          border-radius: 6px;
          color: #e5e7eb;
          font-size: 13px;
        }
        .campaign-note {
          font-size: 12px;
          color: #f59e0b;
          margin-top: 4px;
        }
        .past-campaigns-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .load-campaigns-btn {
          background: #374151;
          color: #e5e7eb;
          border: 1px solid #4b5563;
          border-radius: 6px;
          padding: 7px 14px;
          font-size: 13px;
          cursor: pointer;
        }
        .load-campaigns-btn:hover { background: #4b5563; }
        .load-campaigns-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .close-campaigns-btn {
          background: transparent;
          color: #9ca3af;
          border: 1px solid #374151;
          border-radius: 6px;
          padding: 7px 10px;
          font-size: 12px;
          cursor: pointer;
        }
        .close-campaigns-btn:hover { color: #e5e7eb; border-color: #6b7280; }
        .past-campaigns-list {
          margin-top: 10px;
          border: 1px solid #374151;
          border-radius: 8px;
          overflow: hidden;
          max-height: 320px;
          overflow-y: auto;
        }
        .past-campaign-item {
          padding: 10px 14px;
          border-bottom: 1px solid #1f2937;
          cursor: pointer;
          transition: background 0.15s;
        }
        .past-campaign-item:last-child { border-bottom: none; }
        .past-campaign-item:hover { background: #1f2937; }
        .past-campaign-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }
        .past-campaign-name {
          font-size: 13px;
          font-weight: 600;
          color: #e5e7eb;
        }
        .past-campaign-status {
          font-size: 11px;
          padding: 2px 7px;
          border-radius: 10px;
          background: #1f2937;
        }
        .past-campaign-status.status-completed { color: #34d399; }
        .past-campaign-status.status-running { color: #60a5fa; }
        .past-campaign-status.status-failed { color: #f87171; }
        .past-campaign-status.status-paused { color: #fbbf24; }
        .past-campaign-status.status-created { color: #9ca3af; }
        .past-campaign-status.status-scheduled { color: #c084fc; }
        .past-campaign-meta {
          display: flex;
          gap: 12px;
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 4px;
        }
        .past-campaign-message {
          font-size: 12px;
          color: #9ca3af;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        .past-campaigns-note {
          padding: 10px 14px;
          background: #1f2937;
          border-bottom: 1px solid #374151;
          font-size: 12px;
          color: #9ca3af;
        }
        .past-campaigns-note strong { color: #e5e7eb; }
        .no-campaigns {
          padding: 16px;
          text-align: center;
          color: #6b7280;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
};
